import { useMemo } from 'react';
import { LANGUAGES } from '@/state/member/memberTypes';
import { supportedLangs } from '@/lib/constants/generalConstants';
import { useDispatch } from 'react-redux';
import { profileMenuProps } from '@/components/Hypertracker/Header/HTProfileMenu/HTProfileMenuStyled';
import { Menu, Typography } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { toggleSpinner } from '@/state/general/generalSlice';
import { updateSettings } from '@/state/member/memberSlice';
import useIsMemberAuthorized from '@/hooks/useIsMemberAuthorized';
import { useToastCtx } from '@/context/ToastContext';
import { setSettings } from '@/api/settingsApi';
import { Box } from '@mui/system';
import useLanguageUpdate from '@/hooks/useLanguageUpdate';
import CheckIcon from '@mui/icons-material/Check';

const LanguageMenu = ({ anchorEl, handleClose, open }) => {
    const dispatch = useDispatch();
    const isMemberAuthorized = useIsMemberAuthorized();
    const { toggleToastPromise } = useToastCtx();
    const { currentLanguage } = useLanguageUpdate();

    const languageValues = Object.values(LANGUAGES).map((lang) => ({
        name: supportedLangs[lang]?.label,
        selectValue: lang,
        Flag: supportedLangs[lang]?.Flag,
    }));

    const selectedLanguage = useMemo(() => {
        const selected = languageValues.find((item) => item.selectValue === currentLanguage);
        return selected || languageValues[0];
    }, [languageValues, currentLanguage]);

    const handleLanguageSelect = async (lang) => {
        try {
            toggleSpinner(true);
            if (isMemberAuthorized) {
                await toggleToastPromise({
                    promiseFunc: setSettings,
                    payload: { lang: lang.selectValue as LANGUAGES },
                });
            }
            dispatch(updateSettings({ lang: lang.selectValue as LANGUAGES }));
        } catch (error) {
            console.error(error);
        } finally {
            dispatch(toggleSpinner(false));
        }
    };

    return (
        <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            slotProps={{ paper: profileMenuProps }}
            disableScrollLock
        >
            {languageValues.map(({ name, Flag, selectValue }) => {
                const isSelected = selectValue === selectedLanguage.selectValue;
                return (
                    <MenuItem
                        key={selectValue}
                        selected={isSelected}
                        onClick={() => {
                            handleLanguageSelect({ selectValue });
                            handleClose();
                        }}
                        sx={{
                            backgroundColor: 'transparent !important',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%',
                                gap: '6px',
                                justifyContent: 'flex-start',
                            }}
                        >
                            <Flag width={16} height={10} />
                            <Typography
                                variant={'p12'}
                                color={isSelected ? 'hypertracker.light' : 'text.primary'}
                                component={'p'}
                                sx={{ width: '100%' }}
                            >
                                {name}
                            </Typography>
                            {isSelected && (
                                <CheckIcon
                                    sx={{
                                        color: (theme) => theme.palette.hypertracker.light,
                                        fontSize: 16,
                                    }}
                                />
                            )}
                        </Box>
                    </MenuItem>
                );
            })}
        </Menu>
    );
};

export default LanguageMenu;
